import { useState, useEffect, useCallback } from "react";
import { Volume2, Square } from "lucide-react";

// Text-to-speech using the browser's built-in Speech Synthesis (no keys/cost).
// Senior-friendly "Read aloud" button. Hidden gracefully if unsupported.
export const SpeakButton = ({
  text,
  label = "Read aloud",
  testId = "speak-button",
  className = "",
}) => {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSupported(false);
    }
    return () => {
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const toggle = useCallback(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    synth.cancel(); // stop anything already playing on the page
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.95;
    utter.pitch = 1;
    utter.lang = "en-US";
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utter);
  }, [speaking, text]);

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId}
      aria-label={speaking ? "Stop reading" : label}
      aria-pressed={speaking}
      className={`inline-flex items-center gap-2 h-11 px-5 rounded-full font-semibold transition-colors ${
        speaking
          ? "bg-destructive text-white"
          : "bg-secondary border border-border text-foreground hover:border-accent hover:text-accent"
      } ${className}`}
    >
      {speaking ? <Square className="h-4 w-4 fill-current" /> : <Volume2 className="h-5 w-5" />}
      {speaking ? "Stop" : label}
    </button>
  );
};
